import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, ShieldAlert, Loader } from 'lucide-react';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading, success, error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg(t('verifyEmail.noToken', 'Token de verificación no proporcionado o inválido.'));
      return;
    }

    const verifyToken = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const data = await res.json();
        if (res.ok) {
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMsg(data.detail || t('verifyEmail.error', 'Ocurrió un problema al verificar tu cuenta.'));
        }
      } catch (err) {
        console.error(err);
        setStatus('error');
        setErrorMsg(t('verifyEmail.connError', 'No pudimos conectarnos al servidor. Inténtalo de nuevo.'));
      }
    };

    verifyToken();
  }, [token, t]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-between">
      <Navbar />

      <div className="flex-1 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-3xl p-8 max-w-md w-full shadow-lg border border-gray-100 text-center"
        >
          {status === 'loading' && (
            <div className="py-12">
              <Loader className="w-16 h-16 text-[#36C0C7] animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-[#111827] mb-2">
                {t('verifyEmail.verifyingTitle', 'Verificando tu cuenta')}
              </h2>
              <p className="text-gray-500 text-sm">
                {t('verifyEmail.verifyingDesc', 'Espera un momento mientras validamos tu token de seguridad...')}
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-8">
              <div className="w-20 h-20 bg-green-50 border border-green-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShieldCheck className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-[#111827] mb-3">
                {t('verifyEmail.successTitle', '¡Cuenta verificada!')}
              </h2>
              <p className="text-gray-600 text-sm mb-8 leading-relaxed">
                {t('verifyEmail.successDesc', 'Tu correo ha sido confirmado correctamente. Ahora tienes acceso completo a la plataforma.')}
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-[#134168] hover:bg-[#36C0C7] text-white py-3.5 rounded-full font-bold transition-all"
              >
                {t('verifyEmail.loginBtn', 'Iniciar sesión')}
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="py-8">
              <div className="w-20 h-20 bg-red-50 border border-red-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShieldAlert className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-[#111827] mb-3">
                {t('verifyEmail.errorTitle', 'Fallo de verificación')}
              </h2>
              <p className="text-red-600 text-sm font-medium mb-4">
                {errorMsg}
              </p>
              <p className="text-gray-500 text-xs mb-8 leading-relaxed">
                {t('verifyEmail.errorHelp', 'Si el token expiró, puedes solicitar uno nuevo desde la pantalla de inicio de sesión o perfil.')}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full bg-[#134168] hover:bg-[#1f4e75] text-white py-3.5 rounded-full font-bold transition-all"
                >
                  {t('verifyEmail.goToLogin', 'Ir al inicio de sesión')}
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-3.5 rounded-full font-bold hover:bg-gray-50 transition-all"
                >
                  {t('verifyEmail.goToHome', 'Volver al inicio')}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <div className="h-16" /> {/* Spacer */}
    </div>
  );
}
